/**
 * Phase 3.27 — Buyer-Dashboard Reservierungs-Übersicht.
 * Zeigt alle eigenen Reservierungen (aktive + abgelaufene) mit Live-Countdown,
 * Link zum Inserat und Status-Badge.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, CheckCircle2, AlertCircle, XCircle, FileSignature, Bike, Star, Download } from "lucide-react";
import api, { formatPrice } from "../lib/api";
import { previewPdfResponse } from "../lib/pdfPreview";

function formatCountdown(ms) {
    if (ms <= 0) return "abgelaufen";
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h >= 24) {
        const d = Math.floor(h / 24);
        return `${d}t ${h % 24}h ${m}m`;
    }
    return `${h}h ${m}m`;
}

function statusInfo(r) {
    if (r.status === "pending") {
        const expired = new Date(r.expires_at).getTime() < Date.now();
        if (expired) return { label: "Abgelaufen", cls: "bg-red-100 text-red-700 border-red-200", Icon: AlertCircle };
        return { label: "Aktiv", cls: "bg-green-100 text-green-700 border-green-200", Icon: CheckCircle2 };
    }
    if (r.status === "expired") return { label: "Abgelaufen", cls: "bg-gray-100 text-gray-700 border-gray-200", Icon: XCircle };
    if (r.status === "cancelled") return { label: "Storniert", cls: "bg-gray-100 text-gray-700 border-gray-200", Icon: XCircle };
    return { label: r.status, cls: "bg-gray-100 text-gray-600 border-gray-200", Icon: Clock };
}

export default function ReservationsList() {
    const [items, setItems] = useState(null);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        let alive = true;
        api.get("/reservations/me")
            .then((r) => { if (alive) setItems(r.data?.items || []); })
            .catch(() => { if (alive) setItems([]); });
        return () => { alive = false; };
    }, []);

    // Live tick for countdowns
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(t);
    }, []);

    if (items === null) {
        return (
            <div className="space-y-3" data-testid="reservations-loading">
                {[1, 2].map((i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-sm h-24 animate-pulse" />
                ))}
            </div>
        );
    }

    if (!items.length) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="reservations-empty">
                <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="font-display font-bold text-lg mb-1">Noch keine Reservierungen</h3>
                <p className="text-sm text-gray-600">
                    Sichern Sie sich Fahrzeuge direkt auf der Detailseite mit dem Button „Jetzt reservieren".
                </p>
                <Link to="/search?category=motorcycle" className="inline-block mt-4 px-4 py-2 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] rounded-sm font-bold uppercase tracking-wider text-xs" data-testid="reservations-empty-cta">
                    Inserate ansehen
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="reservations-list">
            <div className="flex items-center justify-between mb-2">
                <h2 className="font-display font-bold text-xl">Meine Reservierungen</h2>
                <span className="text-xs text-gray-500" data-testid="reservations-count">{items.length}</span>
            </div>
            {items.map((r) => {
                const info = statusInfo(r);
                const { Icon } = info;
                const remaining = new Date(r.expires_at).getTime() - now;
                const isActive = r.status === "pending" && remaining > 0;
                const canCancel = r.status === "pending" && remaining > 0;
                const listing = r.listing || {};
                const cover = (listing.images && listing.images[0]) || null;
                const cancel = async () => {
                    if (!window.confirm("Reservierung wirklich stornieren?")) return;
                    try {
                        await api.delete(`/reservations/${r.id}`);
                        setItems((prev) => prev.map((x) => x.id === r.id ? { ...x, status: "cancelled" } : x));
                    } catch (e) {
                        alert(e.response?.data?.detail || "Stornierung fehlgeschlagen");
                    }
                };
                return (
                    <div key={r.id} className="bg-white border border-gray-200 rounded-sm overflow-hidden flex" data-testid={`reservation-row-${r.id}`}>
                        <Link to={`/listing/${r.listing_id}`} className="w-32 h-32 shrink-0 bg-gray-100 flex items-center justify-center" data-testid={`reservation-thumb-${r.id}`} target="_blank" rel="noopener noreferrer">
                            {cover ? (
                                <img src={cover} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <Bike className="w-10 h-10 text-gray-300" />
                            )}
                        </Link>
                        <div className="flex-1 p-4 min-w-0 flex flex-col sm:flex-row gap-3 sm:items-center">
                            <div className="flex-1 min-w-0">
                                <Link to={`/listing/${r.listing_id}`} className="font-bold text-sm hover:underline truncate block" data-testid={`reservation-title-${r.id}`} target="_blank" rel="noopener noreferrer">
                                    {listing.title || `${listing.brand || ""} ${listing.model || ""}`.trim() || "Inserat"}
                                </Link>
                                <div className="text-xs text-gray-600 mt-0.5">
                                    {listing.year && <span>EZ {listing.year}</span>}
                                    {listing.price != null && (
                                        <span className="ml-2 font-mono-num font-bold text-[#111827]">{formatPrice(listing.price)}</span>
                                    )}
                                </div>
                                <div className="text-[10px] text-gray-500 mt-1">
                                    Reserviert: {new Date(r.reserved_at).toLocaleString("de-DE")}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider font-bold ${info.cls}`} data-testid={`reservation-status-${r.id}`}>
                                    <Icon className="w-3 h-3" /> {info.label}
                                </span>
                                {isActive && (
                                    <div className="font-mono-num font-black text-sm text-green-700 mt-1" data-testid={`reservation-countdown-${r.id}`}>
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        {formatCountdown(remaining)}
                                    </div>
                                )}
                                {r.contract_dispatched_at && (
                                    <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-1 justify-end" data-testid={`reservation-contract-${r.id}`}>
                                        <FileSignature className="w-3 h-3" /> Vertrag verschickt
                                    </div>
                                )}
                                <button
                                    onClick={async () => {
                                        try {
                                            const resp = await api.get(`/reservations/${r.id}/contract.pdf`, { responseType: "blob" });
                                            previewPdfResponse(resp, `Kaufvertrag_${r.id.slice(0, 8)}.pdf`);
                                        } catch (_e) {
                                            alert("Kaufvertrag konnte nicht geladen werden.");
                                        }
                                    }}
                                    className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1d4ed8] hover:underline"
                                    data-testid={`reservation-contract-pdf-${r.id}`}
                                    title="Vorausgefüllten Kaufvertrag als PDF herunterladen"
                                >
                                    <Download className="w-3 h-3" /> Vorvertrag PDF
                                </button>
                                {/* Phase 4g.H — Vorvertrag online signieren (Skribble FES/AES eIDAS) */}
                                <button
                                    onClick={async () => {
                                        try {
                                            const init = await api.post(`/reservations/${r.id}/sign`);
                                            const url = init.data?.signing_url;
                                            if (init.data?.mocked) {
                                                alert(`MOCKED-Modus: Skribble-Credentials fehlen.\nSimulierte Signatur-Anfrage erstellt.\nSigning-URL: ${url}\n\nFür echte Signaturen: SKRIBBLE_USERNAME + SKRIBBLE_API_KEY in backend/.env setzen.`);
                                            } else if (url) {
                                                window.open(url, "_blank", "noopener,noreferrer");
                                            } else {
                                                alert("Signatur-Anfrage erstellt — bitte E-Mail-Posteingang prüfen.");
                                            }
                                        } catch (e) {
                                            alert(e?.response?.data?.detail || "Signatur konnte nicht initiiert werden.");
                                        }
                                    }}
                                    className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 hover:underline"
                                    data-testid={`reservation-sign-${r.id}`}
                                    title="Vorvertrag online signieren (Skribble eIDAS FES) — beide Parteien erhalten eine E-Mail-Einladung"
                                >
                                    <FileSignature className="w-3 h-3" /> Online signieren
                                </button>
                                {canCancel && (
                                    <button
                                        onClick={cancel}
                                        className="mt-1 text-[10px] text-red-600 hover:text-red-800 hover:underline"
                                        data-testid={`reservation-cancel-${r.id}`}
                                    >
                                        Stornieren
                                    </button>
                                )}
                                {r.can_review && r.seller?.id && (
                                    <Link
                                        to={`/dealer/${r.seller.id}#reviews`}
                                        className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:text-[#FFCC00]"
                                        data-testid={`reservation-review-cta-${r.id}`}
                                        title={`Bewertung für ${r.seller.company_name || r.seller.name} abgeben`}
                                    >
                                        <Star className="w-3 h-3 fill-[#FFCC00] text-[#FFCC00]" />
                                        Händler bewerten
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
